import type { ApiErrorBody } from '@anidex/shared/types';
import i18n from '../i18n';
import { flagAutomation } from './automation';
import { requestHumanCheck } from './human-check';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: RequestOptions = {}, retried = false): Promise<T> {
  const hasBody = options.body !== undefined;
  const rawBody = options.body instanceof Blob ? options.body : null;
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method: options.method ?? (hasBody ? 'POST' : 'GET'),
      headers: {
        Accept: 'application/json',
        ...(hasBody ? { 'Content-Type': rawBody ? rawBody.type || 'application/octet-stream' : 'application/json' } : {}),
        ...options.headers,
      },
      body: rawBody ?? (hasBody ? JSON.stringify(options.body) : undefined),
      credentials: 'same-origin',
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'network_error', i18n.t('errors:network_error'));
  }

  const text = await response.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!response.ok) {
    const error = (data as ApiErrorBody | null)?.error;
    if (error?.code === 'automation_forbidden') flagAutomation('ai_agent');
    const checkKey = (error?.details as { siteKey?: unknown } | undefined)?.siteKey;
    if (error?.code === 'human_check_required' && typeof checkKey === 'string' && !retried) {
      await requestHumanCheck(checkKey);
      return api<T>(path, options, true);
    }
    throw new ApiError(
      response.status,
      error?.code ?? 'http_error',
      error?.message ?? i18n.t('errors:http_error', { status: response.status }),
      error?.details,
    );
  }
  return data as T;
}

export async function downloadAccountExport(): Promise<void> {
  let response: Response;
  try {
    response = await fetch('/api/me/export', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError(0, 'network_error', i18n.t('errors:network_error'));
  }
  if (!response.ok) {
    const text = await response.text();
    let data: unknown = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }
    const error = (data as ApiErrorBody | null)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'http_error',
      error?.message ?? i18n.t('errors:http_error', { status: response.status }),
      error?.details,
    );
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'anidex-donnees.json';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function queryString(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '' && value !== false) search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const details = error.details && typeof error.details === 'object' ? (error.details as Record<string, unknown>) : {};
    return i18n.t(`errors:${error.code}`, { defaultValue: error.message, status: error.status, ...details });
  }
  if (error instanceof Error) return error.message;
  return i18n.t('errors:unexpected');
}
